<template>
  <v-layout v-if="screener" row justify-center>
    <v-dialog
      :value="seatsDialog"
      @input="reset()"
      persistent
      scrollable
      max-width="50vw"
    >
      <v-card>
        <v-card-title class="px-4">
          <div class="title">{{ $t('screener.upgrade_free_plan.title') }}</div>
          <v-spacer></v-spacer>
          <v-btn
            class="mx-0 px-0"
            small
            dark
            icon
            color="grey"
            @click="reset()"
          >
            <v-icon small>$vuetify.icons.rms_close</v-icon>
          </v-btn></v-card-title
        >
        <v-divider></v-divider>
        <v-card-text style="max-height: 80vh">
          <v-stepper class="elevation-0" v-model="update_step">
            <v-stepper-header
              v-if="screener.screening_type == DEFINES.SCREENING_PLAN_TYPE.FREE"
              flat
              class="elevation-0 px-0"
            >
              <v-stepper-step
                step="1"
                :complete="update_step > 1"
                class="px-3"
              >
                {{$t('screener.pricing_type.title')}}
              </v-stepper-step>
              <v-divider></v-divider>
              <v-stepper-step
                step="2"
                :complete="update_step > 2"
                class="px-3"
                >{{
                  $t('screener.upgrade_step.first_step.title')
                }}</v-stepper-step
              >
              <v-divider></v-divider>
              <v-stepper-step
                step="3"
                :complete="update_step > 3"
                class="px-3"
              >
                {{ $t('screener.upgrade_step.second_step.title') }}
              </v-stepper-step>
            </v-stepper-header>
            <v-stepper-items class="pa-3">
              <v-stepper-content step="1" class="pa-0">
                <PricingTypeSelection
                :pricing_type.sync="screener.pricing_type"
                :isReducedPriceAvailable="!isScreeningPaid(screener)"
                @goToStep="goToStep"
                @redirectTo="redirectTo"
                ></PricingTypeSelection>
                <v-layout class="mt-5">
                  <v-spacer></v-spacer>
                  <v-btn class="ml-0" dark color="grey" @click="reset()">{{
                    $t('rms.button.cancel')
                  }}</v-btn>
                </v-layout>
              </v-stepper-content>
              <v-stepper-content step="2" class="pa-0">
                <ScreenerSeatsPlanner
                  :screener="screener"
                  :company="company"
                  :slots.sync="slots"
                  :total_participants.sync="total_participants"
                  @goToStep="goToStep"
                  @onUpgrade="seatsHandler"
                  @onCancel="reset"
                  ref="screenerSeatsPlanner"
                  :selectedSlot="_findSlotBySeats(screener.limitation)"
                  :minSlot="
                    _findSlotByTotalParticipantUsed(screener.total_registrants)
                  "
                ></ScreenerSeatsPlanner>
                <Faqs
                  class="mt-3"
                  :faqs="DEFINES.PREMIUM_SCREENING_FAQS"
                ></Faqs>
              </v-stepper-content>
              <v-stepper-content step="3" class="pa-0">
                <template
                  v-if="
                    selected_company.card_brand &&
                      selected_company.card_last_four
                  "
                >
                  <v-radio-group
                    hide-details
                    v-model="use_existing_payment_method"
                    row
                    class="mb-4 mt-0"
                    color="primary"
                  >
                    <RadioInput
                      :value="true"
                      :label="$t('screener.use_existing_payment_mthd.title')"
                    ></RadioInput>
                    <RadioInput
                      :value="false"
                      :label="$t('screener.add_payment_mthd.title')"
                    ></RadioInput>
                  </v-radio-group>
                  <div v-show="use_existing_payment_method">
                    <ExistingPaymentMethod></ExistingPaymentMethod>
                  </div>
                  <div v-show="!use_existing_payment_method">
                    <PaymentForm
                      class="mt-3"
                      :payment_details.sync="payment_details"
                      :cardError="cardError"
                      ref="paymentDetailForm"
                    ></PaymentForm>
                    <div class="greyDarken3--text">
                      <div class="pt-1">
                        <i>{{
                          $t('screener.upgrade.payment_method.instruction.1')
                        }}</i>
                      </div>
                      <div class="pt-1">
                        <i>{{
                          $t('screener.upgrade.payment_method.instruction.2')
                        }}</i>
                      </div>
                    </div>
                  </div>
                </template>
                <template v-else>
                  <PaymentForm
                    class="mt-3"
                    :payment_details.sync="payment_details"
                    :cardError="cardError"
                    ref="paymentDetailForm"
                  ></PaymentForm>
                </template>
                <v-layout class="mt-5">
                  <v-btn class="mx-0" dark color="grey" @click="goToStep(2)"
                    ><v-icon small class="px-1"
                      >$vuetify.icons.screener_back</v-icon
                    >{{ $t('screener.btn.back') }}
                  </v-btn>
                  <v-spacer></v-spacer>
                  <v-btn class="ml-0" dark color="grey" @click="reset()">{{
                    $t('rms.button.cancel')
                  }}</v-btn>
                  <v-btn
                    class="mr-0"
                    dark
                    color="secondary"
                    @click="seatsHandler()"
                    >{{ $t('screener.btn.pay_now') +' - '+ getChargeableAmount(screener.pricing_type) }}</v-btn
                  >
                </v-layout>
              </v-stepper-content>
            </v-stepper-items>
          </v-stepper>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-layout>
</template>

<script>
import DEFINES from '@/defines';
import rules from '@/validation';
import i18n from '@/i18n';
import {mapState, mapGetters} from 'vuex';
import ScreenerPlans from '@/components/screener/Plans';
import ScreenerSeatsPlanner from '@/components/screener/SeatsPlanner';
import PaymentForm from '@/components/PaymentForm';
import RadioInput from '@/components/RadioInput';
import ExistingPaymentMethod from '@/components/ExistingPaymentMethod';
import Faqs from '@/components/FAQs';
import PricingTypeSelection from "@/components/screener/PricingTypeSelection";

export default {
  name: 'UpdateScreeningSeats',
  props: {
    seatsDialog: {
      type: Boolean,
      default: false,
    },
    screener: {
      type: Object,
      default: null,
    },
    company:{
      type: Object,
      default: null,
    }
  },
  data() {
    return {
      rules,
      update_step: this.getCurrentStep(),
      slots: [],
      payment_details: null,
      use_existing_payment_method: this.isPaymentMethodExists(),
      total_participants: 0,
      payment_intent_id: null,
      stripe:null,
      card:null,
      cardError:null
    };
  },
  components: {
    ScreenerPlans,
    ScreenerSeatsPlanner,
    PaymentForm,
    RadioInput,
    ExistingPaymentMethod,
    Faqs,
    PricingTypeSelection
  },
  computed: {
    ...mapGetters('company', {
      selected_company: 'getSelectedCompany',
    }),
    ...mapGetters("screener", {
      isScreeningPaid: "isScreeningPaid"
    }),
  },
  mounted() {
    this.stripe = Stripe(DEFINES.STRIPE_KEY);
    this.mountStripeCard();
  },
  methods: {
    mountStripeCard(){
      // Initialize Stripe
      this.elements = this.stripe.elements();

      // Create and mount card element
      this.card = this.elements.create("card", this._stripeOptions());
      this.card.mount("#card-element");

      this.card.on('change', (event) => {
        this.cardError = event.error ? event.error.message : '';
      });
    },
    getCurrentStep(){
      switch (this.screener.screening_type) {
        case DEFINES.SCREENING_PLAN_TYPE.FREE:
          return 1;
        case DEFINES.SCREENING_PLAN_TYPE.PREMIUM:
          return 2;
        default:
          return 1;
      }
    },
    seatsHandler() {
      switch (this.screener.screening_type) {
        case DEFINES.SCREENING_PLAN_TYPE.FREE:
          this.upgrade();
          break;
        case DEFINES.SCREENING_PLAN_TYPE.PREMIUM:
          this.updateSeats();
          break;
        default:
          break;
      }
    },
    goToStep(step) {
      if(!step){
        return;
      }
      this.update_step = step;
    },
    reset() {
      // Reset everything
      this.$emit('update:seatsDialog', false);
      this.goToStep(1);
      this.use_existing_payment_method = true;
      // Steppers first step will not mount payment form
      if (this.$refs.paymentDetailForm) {
        this.$refs.paymentDetailForm.reset();
      }
      this.$refs.screenerSeatsPlanner.reset();
      if(this.screener.screening_type == DEFINES.SCREENING_PLAN_TYPE.FREE){
        this.screener.pricing_type = DEFINES.PREMIUM_SCREENING_PRICING_TYPE.NORMAL;
      }

    },
    async authenticationComplete(event) {
      if (
        event.data != '3DS-authentication-complete' ||
        !this.payment_intent_id
      ) {
        return;
      }

      // mark the purchase paid
      try {
        // Preparing payload
        var payload = {};

        payload.participant_slots = this.slots;
        payload.payment_method = this.payment_details;
        payload.screener_id = this.screener.id;
        payload.use_existing_payment_method = this.use_existing_payment_method;
        payload.payment_intent_id = this.payment_intent_id;

        let response = await this.$store.dispatch(
          'screener/markUpgraded',
          payload
        );
        this.afterPurchase(response);
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      } finally {
        this.payment_intent_id = null;
      }
    },

    async upgrade() {
      try {
        if (
          !this.use_existing_payment_method &&
          this.cardError
        ) {
          return false;
        }
        
        // Preparing payload
        var payload = {};

        payload.participant_slots = this.slots;
        if(!this.use_existing_payment_method && this.card){
          let paymentMethodRes = await this.stripe.createPaymentMethod({
            type: 'card',
            card: this.card,
            billing_details:this.payment_details.billing_details
          });
          payload.payment_method = paymentMethodRes.paymentMethod;
        }
        payload.screener_id = this.screener.id;
        payload.pricing_type = this.screener.pricing_type;
        payload.use_existing_payment_method = this.use_existing_payment_method;

        // Go to upgrade
        var response = await this.$store.dispatch('screener/upgrade', payload);
        if (response.intent) {
          // Let Stripe handle any required 3D Secure challenge in its own managed
          // modal (resolves only once authentication completes). The payment method
          // is already attached to the PaymentIntent server-side.
          let res = await this.stripe.confirmCardPayment(
            response.intent.client_secret,
            {
              payment_method: response.intent.payment_method,
            }
          );

          if (res.error) {
            throw res.error;
          }

          this.payment_intent_id = res.paymentIntent.id;

          if (res.paymentIntent.status != 'succeeded') {
            throw new Error(
              (res.paymentIntent.last_payment_error &&
                res.paymentIntent.last_payment_error.message) ||
                res.paymentIntent.status
            );
          }

          // Payment (and any 3D Secure authentication) complete -> mark upgraded.
          this.authenticationComplete({ data: '3DS-authentication-complete' });
        } else if (response.screener) {
          this.afterPurchase(response);
        }
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      }
    },
    async afterPurchase(response){
      // Assigning to local variable as after upgrade everything gets reset
      var total_participants = this.total_participants;
      this.$emit('updateScreener', response.screener); //screeningType
      this.reset();
      await this.$store.dispatch('app/showMessage', {
        message: i18n.t('screener.seats.upgrade.success', {
          amount: this.getChargeableAmount(response.screener.pricing_type),
          total_participants: total_participants,
        }),
        color: 'success',
      });
    },

    _stripeOptions(){
      return {
        style: {
          base: {
            lineHeight: '24px',
            fontSize: '16px',
            fontFamily: '"Inter", "Helvetica Neue", Helvetica, sans-serif',
            color: '#111827', // dark gray
            '::placeholder': {
              color: '#9ca3af', // neutral placeholder
            },
          },
          invalid: {
            iconColor: '#ef4444',
            color: '#ef4444',
          },
        },
        hidePostalCode: true,
      }
    },

    async updateSeats() {
      try {
        // If no changes made in seats, do nothing
        if (this.total_participants == this.screener.limitation) {
          return;
        }
        // Check it's downgrade or upgrade
        var update_mode =
          this.total_participants > this.screener.limitation
            ? DEFINES.PREMIUM_SCREENING_SEAT_UPDATE_MODE.UPGRADE
            : DEFINES.PREMIUM_SCREENING_SEAT_UPDATE_MODE.DOWNGRADE;

        var vue = this;

        var current_slot_id = this._findSlotBySeats(this.screener.limitation);
        var allotted_slots = [];

        // Getting all allotted slots for the screening
        DEFINES.PREMIUM_SCREENING_PARTICIPANT_SLOTS[this.screener.pricing_type].every(function(
          slot,
          index
        ) {
          if (slot.active) {
            allotted_slots.push(slot.id);
          }
          return slot.id !== current_slot_id; // Returning true terminates the loop
        });

        // Finds newly added slots in case of upgrade or removed slots in case of downgrade
        switch (update_mode) {
          case DEFINES.PREMIUM_SCREENING_SEAT_UPDATE_MODE.UPGRADE:
            var updated_slots = this.slots.filter(
              (slot) => !allotted_slots.includes(slot)
            );
            break;
          case DEFINES.PREMIUM_SCREENING_SEAT_UPDATE_MODE.DOWNGRADE:
            var updated_slots = allotted_slots.filter(
              (slot) => !this.slots.includes(slot)
            );
            break;
          default:
            break;
        }

        // Preparing payload
        var payload = {};

        payload.update_mode = update_mode;
        payload.screener_id = this.screener.id;
        payload.limitation = this.total_participants;
        payload.participant_slots = updated_slots;

        // Go to update
        var response = await this.$store.dispatch(
          'screener/updateSeats',
          payload
        );
        this.$emit('updateScreener', response.screener);
        this.reset();
        await this.$store.dispatch('app/showMessage', {
          message: i18n.t('screener.seats.update.success', {
            total_participants: payload.limitation,
          }),
          color: 'success',
        });
      } catch (error) {
        this.$store.dispatch('app/showMessage', {
          message: error.message,
          color: 'error',
        });
      }
    },
    isPaymentMethodExists() {
      return this.$store.state.company.company.card_brand &&
        this.$store.state.company.company.card_last_four
        ? true
        : false;
    },
    // Returns slot Id according to current seat limitation
    _findSlotBySeats(seats) {
      var _slot = DEFINES.PREMIUM_SCREENING_PARTICIPANT_SLOTS[this.screener.pricing_type].find((slot) => {
        return slot.active && slot.consolidated_value == seats;
      });
      return _slot ? _slot.id : DEFINES.PREMIUM_SCREENING_DEFAULT_SLOT;
    },
    // Returns slot Id according to used number of participants for a screening
    _findSlotByTotalParticipantUsed(participants) {
      var _slot = DEFINES.PREMIUM_SCREENING_PARTICIPANT_SLOTS[this.screener.pricing_type].find((slot) => {
        return (
          slot.active &&
          participants >= slot.min_value &&
          participants <= slot.max_value
        );
      });
      return _slot ? _slot.id : DEFINES.PREMIUM_SCREENING_DEFAULT_SLOT;
    },
    getChargeableAmount(pricingType){
      var amount = 0;
      switch (pricingType) {
        case DEFINES.PREMIUM_SCREENING_PRICING_TYPE.NORMAL:
          amount =  DEFINES.PREMIUM_SCREENING_BASE_CHARGEABLE_AMOUNT;
          break;
        case DEFINES.PREMIUM_SCREENING_PRICING_TYPE.REDUCED:
          amount = DEFINES.PREMIUM_SCREENING_REDUCED_BASE_CHARGEABLE_AMOUNT;
          break;
      }
      return DEFINES.PREMIUM_SCREENING_BASE_CURRENCY_SYMBOL+amount;
    },
    redirectTo(){
      this.reset();
      this.$emit("redirectTo");
    }
  },
};
</script>

<style>
.stripe-card {
  border: 1px solid #d1d5db;   /* light gray border */
  border-radius: 8px;          /* rounded corners */
  padding: 12px 16px;          /* spacing inside */
  background-color: #fff;      /* white background */
  box-shadow: 0 2px 4px rgba(0,0,0,0.05); /* subtle shadow */
  transition: all 0.2s ease-in-out;
}

.stripe-card.StripeElement--focus {
  border-color: #6366f1;       /* highlight color (indigo) */
  box-shadow: 0 0 0 3px rgba(99,102,241,0.2);
}

.stripe-card.StripeElement--invalid {
  border-color: #ef4444;       /* red on error */
}
</style>
